/**
 * Generic paginated CRUD factory for backend entities.
 * Each entity is configured with an API path and field mappers,
 * then gets list/create/update/delete/import/count operations for free.
 */

import { apiFetch } from './fetch';
import { API } from '../constants';
import { buildCsvText } from '../utils/csvBuild';
import type { Customer, Vendor, Language, Service, ProfitCentre, SubService } from '../types/dvms.types';

// ─── Shared types ────────────────────────────────────────────────────────────

export interface PageParams {
    /** 0-based page index */
    page: number;
    limit: number;
}

export interface PageResult<T> {
    data: T[];
    total: number;
}

/** Wraps paginated list responses from the backend. */
export interface PageResponse<R> {
    data: R[];
    total: number;
    page: number;
    limit: number;
}

/** One rejected CSV row: its 1-based row number (header is row 1) and the reason. */
export interface ImportRowFailure {
    row: number;
    error: string;
}

/** Result returned by the backend bulk CSV import endpoint. */
export interface BulkImportResult {
    total: number;
    created: number;
    updated: number;
    failed: number;
    sourceFileURL?: string;
    summaryFileURL?: string;
    /** Per-row reasons, always present when rows failed (the summary file is best-effort). */
    failures?: ImportRowFailure[];
}

// ─── CRUD factory ────────────────────────────────────────────────────────────

interface EntityApiConfig<T, R> {
    apiPath: string;
    toFrontend: (r: R) => T;
    toCreateBody: (entity: T) => unknown;
    toUpdateBody: (entity: Partial<T>) => unknown;
    /**
     * When provided, importMany sends a single CSV to the bulk `POST {apiPath}/csv`
     * endpoint instead of individual POST requests. Maps a FE entity to a row
     * keyed by backend CSV column headers.
     */
    toCsvRow?: (entity: T) => Record<string, string>;
}

/** Maximum parallel requests during batch import (fallback for entities without bulk CSV). */
const IMPORT_CONCURRENCY = 5;

function createEntityApi<T extends { id: string }, R = T>(config: EntityApiConfig<T, R>) {
    return {
        /** Fetches all records in a single request. */
        list: async (): Promise<T[]> => {
            const resp = await apiFetch<PageResponse<R>>(`${config.apiPath}?page=0&limit=50`);
            return resp.data.map(config.toFrontend);
        },
        create: async (payload: T): Promise<T> => {
            const resp = await apiFetch<R>(config.apiPath, {
                method: 'POST',
                body: JSON.stringify(config.toCreateBody(payload)),
            });
            return config.toFrontend(resp);
        },
        update: async (id: string, payload: Partial<T>): Promise<void> => {
            await apiFetch<R>(`${config.apiPath}/${id}`, {
                method: 'PUT',
                body: JSON.stringify(config.toUpdateBody(payload)),
            });
        },
        del: async (id: string): Promise<void> => {
            await apiFetch(`${config.apiPath}/${id}`, { method: 'DELETE' });
        },
        /**
         * Bulk import via the backend CSV endpoint when {@link toCsvRow} is
         * configured (atomic transaction with SAVEPOINTs). Falls back to
         * parallel individual POSTs otherwise.
         */
        importMany: async (payload: T[]): Promise<BulkImportResult> => {
            if (config.toCsvRow && payload.length > 0) {
                const [first] = payload;
                if (!first) {
                    throw new Error('Unreachable: payload.length > 0 already checked');
                }
                const toCsvRow = config.toCsvRow;
                const headers = Object.keys(toCsvRow(first));
                const rows = payload.map((item) => toCsvRow(item));
                return apiFetch<BulkImportResult>(`${config.apiPath}/csv`, {
                    method: 'POST',
                    body: buildCsvText(headers, rows),
                    headers: { 'Content-Type': 'text/csv' },
                });
            }

            // Fallback: individual POST requests in parallel batches.
            const result: BulkImportResult = { total: payload.length, created: 0, updated: 0, failed: 0 };
            for (let i = 0; i < payload.length; i += IMPORT_CONCURRENCY) {
                const batch = payload.slice(i, i + IMPORT_CONCURRENCY);
                const settled = await Promise.allSettled(
                    batch.map((item) =>
                        apiFetch<R>(config.apiPath, {
                            method: 'POST',
                            body: JSON.stringify(config.toCreateBody(item)),
                        }),
                    ),
                );
                for (const s of settled) {
                    if (s.status === 'fulfilled') {
                        result.created++;
                    } else {
                        result.failed++;
                    }
                }
            }
            // If everything failed, throw so callers see an error.
            if (result.created === 0 && result.failed > 0) {
                throw new Error(`All ${result.failed} records failed to import`);
            }
            return result;
        },
        listPaged: async (params: PageParams): Promise<{ data: T[]; total: number }> => {
            const resp = await apiFetch<PageResponse<R>>(`${config.apiPath}?page=${params.page}&limit=${params.limit}`);
            return { data: resp.data.map(config.toFrontend), total: resp.total };
        },
        count: async (): Promise<number> => {
            const resp = await apiFetch<{ count: number }>(`${config.apiPath}/count`);
            return resp.count;
        },
        search: async (query: string, signal?: AbortSignal): Promise<T[]> => {
            const opts: RequestInit = {};
            if (signal) {
                opts.signal = signal;
            }
            const result = await apiFetch<R[]>(`${config.apiPath}/search?filter=${encodeURIComponent(query)}`, opts);
            return Array.isArray(result) ? result.map(config.toFrontend) : [];
        },
    };
}

// ─── Backend response interfaces ─────────────────────────────────────────────

interface BackendLanguageResponse {
    id: string;
    displayId?: string;
    languageName: string;
    isoCode: string;
    isActive?: boolean;
    createdAt?: string;
    createdByName?: string;
}
interface BackendNameOnlyResponse {
    id: string;
    displayId?: string;
    name: string;
    createdAt?: string;
    createdByName?: string;
}

// ─── Customers ───────────────────────────────────────────────────────────────

const customerApi = createEntityApi<Customer, BackendNameOnlyResponse>({
    apiPath: API.CUSTOMERS,
    toFrontend: (r) => ({
        id: r.id,
        ...(r.displayId ? { displayId: r.displayId } : {}),
        name: r.name,
        ...(r.createdAt ? { createdAt: r.createdAt } : {}),
        ...(r.createdByName ? { createdByName: r.createdByName } : {}),
    }),
    toCreateBody: (e) => ({ name: e.name }),
    toUpdateBody: (e) => ({ name: e.name }),
    toCsvRow: (e) => ({ id: e.id, name: e.name }),
});
export const listCustomers = customerApi.list;
export const createCustomer = customerApi.create;
export const updateCustomer = customerApi.update;
export const deleteCustomer = customerApi.del;
export const importCustomers = customerApi.importMany;
export const listCustomersPaged = customerApi.listPaged;
export const countCustomers = customerApi.count;
export const searchCustomers = customerApi.search;

// ─── Vendors ─────────────────────────────────────────────────────────────────

const vendorApi = createEntityApi<Vendor, BackendNameOnlyResponse>({
    apiPath: API.VENDORS,
    toFrontend: (r) => ({
        id: r.id,
        ...(r.displayId ? { displayId: r.displayId } : {}),
        name: r.name,
        ...(r.createdAt ? { createdAt: r.createdAt } : {}),
        ...(r.createdByName ? { createdByName: r.createdByName } : {}),
    }),
    toCreateBody: (e) => ({ name: e.name }),
    toUpdateBody: (e) => ({ name: e.name }),
    toCsvRow: (e) => ({ id: e.id, name: e.name }),
});
export const listVendors = vendorApi.list;
export const createVendor = vendorApi.create;
export const updateVendor = vendorApi.update;
export const deleteVendor = vendorApi.del;
export const importVendors = vendorApi.importMany;
export const listVendorsPaged = vendorApi.listPaged;
export const countVendors = vendorApi.count;

// ─── Languages ───────────────────────────────────────────────────────────────

const languageApi = createEntityApi<Language, BackendLanguageResponse>({
    apiPath: API.LANGUAGES,
    toFrontend: (r) => ({
        id: r.id,
        ...(r.displayId ? { displayId: r.displayId } : {}),
        name: r.languageName,
        code: r.isoCode,
        ...(r.createdAt ? { createdAt: r.createdAt } : {}),
        ...(r.createdByName ? { createdByName: r.createdByName } : {}),
    }),
    toCreateBody: (e) => ({ languageName: e.name, isoCode: e.code }),
    toUpdateBody: (e) => ({ languageName: e.name, isoCode: e.code }),
    toCsvRow: (e) => ({ id: e.id, name: e.name, isoCode: e.code }),
});
export const listLanguages = languageApi.list;
export const createLanguage = languageApi.create;
export const updateLanguage = languageApi.update;
export const deleteLanguage = languageApi.del;
export const importLanguages = languageApi.importMany;
export const listLanguagesPaged = languageApi.listPaged;
export const countLanguages = languageApi.count;
export const fetchLanguages = languageApi.list;

// ─── Services ────────────────────────────────────────────────────────────────

const serviceApi = createEntityApi<Service, BackendNameOnlyResponse>({
    apiPath: API.SERVICES,
    toFrontend: (r) => ({
        id: r.id,
        ...(r.displayId ? { displayId: r.displayId } : {}),
        name: r.name,
        ...(r.createdAt ? { createdAt: r.createdAt } : {}),
        ...(r.createdByName ? { createdByName: r.createdByName } : {}),
    }),
    toCreateBody: (e) => ({ name: e.name }),
    toUpdateBody: (e) => ({ name: e.name }),
    toCsvRow: (e) => ({ id: e.id, name: e.name }),
});
export const listServices = serviceApi.list;
export const createService = serviceApi.create;
export const updateService = serviceApi.update;
export const deleteService = serviceApi.del;
export const importServices = serviceApi.importMany;
export const listServicesPaged = serviceApi.listPaged;
export const countServices = serviceApi.count;
export const searchServices = serviceApi.search;

// ─── Profit Centres ──────────────────────────────────────────────────────────

interface BackendProfitCentreResponse extends BackendNameOnlyResponse {
    currency: string;
}

const profitcentreApi = createEntityApi<ProfitCentre, BackendProfitCentreResponse>({
    apiPath: API.PROFIT_CENTRES,
    toFrontend: (r) => ({
        id: r.id,
        ...(r.displayId ? { displayId: r.displayId } : {}),
        name: r.name,
        currency: r.currency,
        ...(r.createdAt ? { createdAt: r.createdAt } : {}),
        ...(r.createdByName ? { createdByName: r.createdByName } : {}),
    }),
    toCreateBody: (e) => ({ name: e.name, currency: e.currency }),
    toUpdateBody: (e) => ({ name: e.name, currency: e.currency }),
    toCsvRow: (e) => ({ id: e.id, name: e.name, currency: e.currency }),
});
export const listProfitCentres = profitcentreApi.list;
export const createProfitCentre = profitcentreApi.create;
export const updateProfitCentre = profitcentreApi.update;
export const deleteProfitCentre = profitcentreApi.del;
export const importProfitCentres = profitcentreApi.importMany;
export const listProfitCentresPaged = profitcentreApi.listPaged;
export const countProfitCentres = profitcentreApi.count;
export const searchProfitCentres = profitcentreApi.search;

// ─── Sub Services ────────────────────────────────────────────────────────────

interface BackendSubServiceResponse extends BackendNameOnlyResponse {
    isOverage?: boolean;
}

const subserviceApi = createEntityApi<SubService, BackendSubServiceResponse>({
    apiPath: API.SUB_SERVICES,
    toFrontend: (r) => ({
        id: r.id,
        ...(r.displayId ? { displayId: r.displayId } : {}),
        name: r.name,
        // `?? false` covers an API that predates the field (deploy skew).
        isOverage: r.isOverage ?? false,
        ...(r.createdAt ? { createdAt: r.createdAt } : {}),
        ...(r.createdByName ? { createdByName: r.createdByName } : {}),
    }),
    toCreateBody: (e) => ({ name: e.name, isOverage: e.isOverage }),
    // Sent only when known: a name-only edit must not clear the type.
    toUpdateBody: (e) => ({ name: e.name, ...(e.isOverage === undefined ? {} : { isOverage: e.isOverage }) }),
    toCsvRow: (e) => ({ id: e.id, name: e.name }),
});
export const listSubServices = subserviceApi.list;
export const createSubService = subserviceApi.create;
export const updateSubService = subserviceApi.update;
export const deleteSubService = subserviceApi.del;
export const importSubServices = subserviceApi.importMany;
export const listSubServicesPaged = subserviceApi.listPaged;
export const countSubServices = subserviceApi.count;
